/**
 * @file Checkboxes.tsx
 *
 * @fileoverview Renders the GDPR consents checkboxes.
 */
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { GdprConsentInfo } from '~/jobiqo/types/types';
import styles from './gdprCheckboxes.module.scss';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';

export const GdprConsentCheckboxes = () => {
  const { control } = useFormContext();
  const {
    getTermFormFieldName,
    isModuleDisabled,
    isNewsletterChecked,
    setIsNewsletterChecked,
    consentInfos,
    requiredConsentInfos,
    errorList,
    consentsChecked,
    consentToggle,
  } = useGdprConsentContext();

  if (isModuleDisabled) {
    return null;
  }

  useEffect(() => {
    return () => {
      // Unset any consents that have been given after teardown.
      consentInfos.forEach((consentInfo) => {
        if (consentsChecked.includes(consentInfo.id)) {
          consentToggle(consentInfo.id);
        }
      });
    };
  }, [consentInfos]);

  return (
    <>
      <Spacing />
      {errorList && (
        <>
          {errorList}
          <Spacing />
        </>
      )}
      {consentInfos.map((consentInfo: GdprConsentInfo) => {
        const Text = (
          <div className={styles.textWrapper}>
            <WYSIWYGText content={consentInfo.text} format="filtered_html" />
          </div>
        );

        const fieldName = getTermFormFieldName(consentInfo);

        return (
          <div key={consentInfo.id}>
            {consentInfo.optIn ? (
              <>
                <Controller
                  name={fieldName}
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <Checkbox
                        {...field}
                        id={field.name}
                        type="checkbox"
                        checked={consentsChecked.includes(consentInfo.id)}
                        // @todo Update lib prop type.
                        label={Text as any}
                        onClick={() => {
                          if (consentInfo.type === 'NEWSLETTER') {
                            setIsNewsletterChecked(!isNewsletterChecked);
                          }
                          consentToggle(consentInfo.id);
                        }}
                        required={requiredConsentInfos.includes(consentInfo)}
                        error={!!error && isTouched}
                        data-test-id="term-checkbox"
                      />
                      {error ? (
                        <>
                          <Spacing />
                          <InlineError message={error.message} />
                        </>
                      ) : null}
                    </>
                  )}
                />
              </>
            ) : (
              Text
            )}
            <Spacing />
          </div>
        );
      })}
    </>
  );
};

export default GdprConsentCheckboxes;
